import { z } from 'zod';
import { rsosApi, baseQueryArgs } from '../rsosApi';

/**
 * Zod schemas for known Midas flow responses, keyed by flow name.
 *
 * Each entry drives both the TypeScript response type and runtime shape
 * validation. Flows without a registered schema are accepted as-is with
 * no validation guarantee.
 */
export const MidasFlowSchemas = {
  international_callback: z.object({
    job_id: z.string(),
    target_session_id: z.string(),
    international_callback_phone: z.string(),
  }),
} satisfies Record<string, z.ZodTypeAny>;

/** Response types for all registered Midas flows, derived from their schemas. */
export type MidasFlowResponseMap = {
  [K in keyof typeof MidasFlowSchemas]: z.infer<(typeof MidasFlowSchemas)[K]>;
};

/** Maps a flow name to its registered response type, or `Record<string, unknown>`
 *  for flows without a schema entry. */
type MidasFlowResult<TFlow extends string> =
  TFlow extends keyof MidasFlowResponseMap
    ? MidasFlowResponseMap[TFlow]
    : Record<string, unknown>;

export interface MidasFlowArgs {
  /** Name of the Midas flow to trigger. */
  callflow: string;
  /** Variables forwarded to the flow. */
  variables: Record<string, unknown>;
  /**
   * How long in milliseconds a successful response may be reused before a new
   * POST is issued. Omit or set to `0` to always fetch a fresh response.
   *
   * - `0` — fetch a fresh response on every call (default)
   * - `Infinity` — cache indefinitely once a response has been received
   * - any positive number — cache until that many ms have elapsed since the
   *   last successful response
   */
  cacheTtlMs?: number;
}

/**
 * RTK Query endpoint that posts to the Midas flow trigger. All flows share
 * this single endpoint — the flow name and variables form the cache key, while
 * `cacheTtlMs` controls staleness independently without affecting the key.
 */
const midasFlowApi = rsosApi.injectEndpoints({
  endpoints: builder => ({
    triggerMidasFlow: builder.query<Record<string, unknown>, MidasFlowArgs>({
      query: ({ callflow, variables }) => ({
        url: 'v1/rem/internal_auth/trigger',
        method: 'POST',
        body: { callflow, blocking: true, variables },
        ...baseQueryArgs,
      }),

      // cacheTtlMs is cache config, not call identity — keep it out of the key
      // so the same flow + variables always map to the same cache entry regardless
      // of which TTL the caller requested.
      serializeQueryArgs: ({ queryArgs: { callflow, variables } }) =>
        JSON.stringify({ callflow, variables }),

      // Eviction is managed via preferCacheValue in useLazyMidasFlow rather than
      // by automatic time-based eviction.
      keepUnusedDataFor: Infinity,
    }),
  }),
});

interface ParseResult<TFlow extends string> {
  data: MidasFlowResult<TFlow>;
  validationError: z.ZodError | undefined;
}

function parseFlowData<TFlow extends string>(
  data: Record<string, unknown>,
  flowName: string | undefined,
): ParseResult<TFlow> {
  const schema = MidasFlowSchemas[flowName as keyof typeof MidasFlowSchemas];

  if (!schema)
    return { data: data as MidasFlowResult<TFlow>, validationError: undefined };

  const outcome = schema.safeParse(data);
  if (outcome.success) {
    return {
      data: outcome.data as MidasFlowResult<TFlow>,
      validationError: undefined,
    };
  }

  return {
    data: data as MidasFlowResult<TFlow>,
    validationError: outcome.error,
  };
}

/**
 * Hook for triggering a Midas flow and receiving a typed response.
 *
 * Pass the flow name as the type parameter to resolve `data` to the registered
 * response shape. The response is validated at runtime against the registered
 * Zod schema; if validation fails, `validationError` is populated and `data`
 * contains the raw response. Flows without a registered schema
 * skip validation and type `data` as `Record<string, unknown>`.
 *
 * Cache behavior is controlled by `cacheTtlMs` in the trigger args — see
 * {@link MidasFlowArgs} for details.
 *
 * @example
 * const [trigger, { data, isFetching, error, validationError }] =
 *   useLazyMidasFlow<'international_callback'>();
 * // data is { job_id: string; target_session_id: string; international_callback_phone: string } | undefined
 */
export function useLazyMidasFlow<TFlow extends string = string>() {
  const [rawTrigger, result] = midasFlowApi.useLazyTriggerMidasFlowQuery();

  const trigger = (args: MidasFlowArgs) => {
    const ttl = args.cacheTtlMs ?? 0;
    const preferCache =
      ttl !== 0 &&
      result.fulfilledTimeStamp !== undefined &&
      Date.now() - result.fulfilledTimeStamp < ttl;
    return rawTrigger(args, preferCache);
  };

  const { data, validationError } =
    result.data !== undefined
      ? parseFlowData<TFlow>(result.data, result.originalArgs?.callflow)
      : { data: undefined, validationError: undefined };

  return [trigger, { ...result, data, validationError }] as const;
}
